import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import PsychologyIcon from '@mui/icons-material/Psychology';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AlbumIcon from '@mui/icons-material/Album';
import BarChartIcon from '@mui/icons-material/BarChart';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

export default function AiInsightsDialog({ open, onClose, item, stationId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const title = item?.title || item?.asset?.title || '';
  const artist = item?.artist ?? item?.asset?.artist ?? item?.asset?.metadata?.artist;
  const artistLabel = artist != null ? (Array.isArray(artist) ? artist.join(', ') : String(artist)) : '';

  useEffect(() => {
    if (!open || !item) {
      setData(null);
      setError(null);
      setLoading(true);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);

    const fetchInsights = async () => {
      try {
        const res = await axios.post(
          `${API_URL}/api/ai/insights`,
          {
            title: title || undefined,
            artist: artistLabel || undefined,
            station_id: stationId || undefined,
          },
          { withCredentials: true, timeout: 25000 }
        );
        if (!cancelled && res.data) {
          setData(res.data);
        }
      } catch (err) {
        if (!cancelled) {
          const msg = err.code === 'ECONNABORTED'
            ? 'That took too long. Try again — Insights should normally return in a couple of seconds.'
            : err.response?.data?.message || err.message || 'Failed to load insights.';
          setError(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchInsights();
    return () => { cancelled = true; };
  }, [open, item, title, artistLabel, stationId]);

  const hasMetadata = data && (data.releaseYear || data.chartUK || data.chartUSA || data.album);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'var(--rs-paper)',
          border: '1px solid var(--rs-border)',
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: '#22d3ee',
          borderBottom: '1px solid var(--rs-border)',
          pb: 1.5,
        }}
      >
        <PsychologyIcon />
        AI Insights
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {item && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {title}
              {artistLabel ? ` — ${artistLabel}` : ''}
            </Typography>
          </Box>
        )}
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
            <CircularProgress size={40} sx={{ color: '#22d3ee', mb: 2 }} />
            <Typography variant="body1" color="text.primary" align="center">
              Looking this up…
            </Typography>
          </Box>
        )}
        {!loading && error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}
        {!loading && !error && data?.insights && (
          <>
            {hasMetadata && (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  mb: 2,
                  p: 1.5,
                  bgcolor: 'rgba(0, 212, 255, 0.06)',
                  borderRadius: 1.5,
                  border: '1px solid rgba(0, 212, 255, 0.15)',
                }}
              >
                {data.releaseYear && (
                  <Chip
                    icon={<CalendarTodayIcon sx={{ fontSize: 16 }} />}
                    label={`Released: ${data.releaseYear}`}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.08)',
                      color: 'var(--rs-text)',
                      '& .MuiChip-icon': { color: '#22d3ee' },
                    }}
                  />
                )}
                {data.chartUK && (
                  <Chip
                    icon={<BarChartIcon sx={{ fontSize: 16 }} />}
                    label={`UK: ${data.chartUK}`}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.08)',
                      color: 'var(--rs-text)',
                      '& .MuiChip-icon': { color: '#ff6b6b' },
                    }}
                  />
                )}
                {data.chartUSA && (
                  <Chip
                    icon={<BarChartIcon sx={{ fontSize: 16 }} />}
                    label={`US: ${data.chartUSA}`}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.08)',
                      color: 'var(--rs-text)',
                      '& .MuiChip-icon': { color: '#4dabf7' },
                    }}
                  />
                )}
                {data.album && (
                  <Chip
                    icon={<AlbumIcon sx={{ fontSize: 16 }} />}
                    label={data.album}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.08)',
                      color: 'var(--rs-text)',
                      '& .MuiChip-icon': { color: '#a78bfa' },
                    }}
                  />
                )}
              </Box>
            )}
            {hasMetadata && <Divider sx={{ borderColor: 'var(--rs-border)', mb: 2 }} />}
            <Typography
              variant="body1"
              sx={{ color: 'text.primary', whiteSpace: 'pre-wrap' }}
            >
              {data.insights}
            </Typography>
          </>
        )}
        {!loading && !error && !data?.insights && (
          <Typography variant="body2" color="text.secondary">
            No insights were returned. Check that Grok is configured in Settings → AI Features.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid var(--rs-border)', p: 2 }}>
        <Button onClick={onClose} variant="contained" sx={{ bgcolor: 'var(--rs-border)', '&:hover': { bgcolor: '#3a4456' } }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
